import { useState, FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { commitOnboarding, type OnboardingInput } from "@/lib/supabase/onboarding";
import { lookupCr, type CrRegistryHit } from "@/lib/supabase/crRegistry";
import type { PropertyType, MembershipRole } from "@/lib/supabase/types";

/**
 * 3-step self-serve onboarding wizard (Sprint 0).
 *
 * Structure ported from eVisa /register/company, but government gates
 * (CR lookup, National ID lookup, SMS OTP, 6 corporate documents, 2-3
 * day approval SLA) are dropped. A SaaS sign-up should take under 5 min.
 *
 *   Step 1 — Company      (name + admin email + first property)
 *   Step 2 — Team         (owner auto-seeded + optional invites)
 *   Step 3 — Review       (password + T&Cs + submit)
 *
 * On submit we hit `commitOnboarding()` which atomically creates:
 *   auth.users → organizations → properties → memberships.
 * Supabase's default email-confirmation setting means the user must
 * confirm their email before they can sign in; we show that state.
 */

type Lang = "en" | "ar";
type Step = 1 | 2 | 3;

type InviteRow = { email: string; role: Exclude<MembershipRole, "owner"> };

function useRegisterLang() {
  const [lang, setLang] = useState<Lang>(() => (localStorage.getItem("diyafa_lang") as Lang) ?? "en");
  const toggleLang = () => setLang((p) => {
    const next = p === "en" ? "ar" : "en";
    localStorage.setItem("diyafa_lang", next);
    return next;
  });
  const isAr = lang === "ar";
  const t = (en: string, ar: string) => (isAr ? ar : en);
  return { isAr, toggleLang, t };
}

export default function RegisterPage() {
  const { refreshMemberships } = useAuth();
  const navigate = useNavigate();
  const { isAr, toggleLang, t } = useRegisterLang();

  // Wizard state
  const [step, setStep] = useState<Step>(1);

  // Step 1 — CR lookup + company
  // crNumber drives the lookup. After a hit, we prefill the company
  // fields but still let the user tweak them before continuing.
  const [crNumber, setCrNumber] = useState("");
  const [crHit, setCrHit] = useState<CrRegistryHit | null>(null);
  const [crMiss, setCrMiss] = useState(false);          // set true after a failed lookup
  const [crManualEntry, setCrManualEntry] = useState(false); // "I'll enter my details manually"
  const [crLookupLoading, setCrLookupLoading] = useState(false);

  const [companyName, setCompanyName] = useState("");
  const [companyNameAr, setCompanyNameAr] = useState("");
  const [companyEmail, setCompanyEmail] = useState("");
  const [companyPhone, setCompanyPhone] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [propertyType, setPropertyType] = useState<PropertyType>("hotel");
  const [propertyName, setPropertyName] = useState("");

  // Step 2 — team
  const [ownerName, setOwnerName] = useState("");
  const [invites, setInvites] = useState<InviteRow[]>([]);
  const [newInviteEmail, setNewInviteEmail] = useState("");
  const [newInviteRole, setNewInviteRole] = useState<InviteRow["role"]>("manager");

  // Step 3 — password + submit
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Async state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const inputClass = `w-full px-4 py-3 rounded-xl text-sm border border-line-strong bg-white outline-none focus:border-gold transition-colors text-ink placeholder-[#B0A898] ${isAr ? "font-tajawal text-right" : ""}`;
  const labelClass = `block text-sm font-medium text-ink-soft mb-1.5 ${isAr ? "font-tajawal" : ""}`;

  // ---------- CR lookup -----------------------------------------

  const runCrLookup = async () => {
    setError("");
    setCrMiss(false);
    setCrHit(null);
    if (!crNumber.trim()) {
      return setError(t("Enter your CR number to continue.", "أدخل رقم السجل التجاري للمتابعة."));
    }
    setCrLookupLoading(true);
    try {
      const hit = await lookupCr(crNumber.trim());
      if (!hit) {
        setCrMiss(true);
        return;
      }
      // Populate defaults from the registry; user can still edit before
      // proceeding to step 2.
      setCrHit(hit);
      setCompanyName(hit.name_en);
      setCompanyNameAr(hit.name_ar ?? "");
      setCompanyPhone(hit.phone ?? "");
      setCompanyAddress(hit.address ?? "");
      setPropertyType(hit.suggested_property_type);
      setPropertyName(hit.name_en);           // sensible first-property default
      if (!ownerName && hit.owner_name) setOwnerName(hit.owner_name);
    } catch (e: any) {
      setError(e?.message || t("Lookup failed. Try again.", "فشل البحث. حاول مرة أخرى."));
    } finally {
      setCrLookupLoading(false);
    }
  };

  const switchToManualEntry = () => {
    setCrManualEntry(true);
    setCrMiss(false);
    setError("");
  };

  // ---------- Validation per step -------------------------------

  function validateStep1(): string | null {
    // Must have either a verified CR hit OR chosen manual entry.
    if (!crHit && !crManualEntry) {
      return t("Please look up your CR or choose 'Enter manually'.",
               "يرجى البحث عن سجلك التجاري أو اختر 'إدخال يدوي'.");
    }
    if (!companyName.trim()) return t("Company name is required.", "اسم الشركة مطلوب.");
    if (!companyEmail.trim() || !/^\S+@\S+\.\S+$/.test(companyEmail)) {
      return t("A valid admin email is required.", "يرجى إدخال بريد إلكتروني صالح.");
    }
    if (!propertyName.trim()) {
      return t("First property name is required.", "اسم المنشأة الأولى مطلوب.");
    }
    return null;
  }

  function validateStep2(): string | null {
    if (!ownerName.trim()) return t("Owner full name is required.", "اسم المالك الكامل مطلوب.");
    return null;
  }

  function validateStep3(): string | null {
    if (password.length < 8) return t("Password must be at least 8 characters.", "كلمة المرور 8 أحرف على الأقل.");
    if (password !== confirmPassword) return t("Passwords do not match.", "كلمتا المرور غير متطابقتين.");
    if (!termsAccepted) return t("Please accept the terms to continue.", "يرجى قبول الشروط للمتابعة.");
    return null;
  }

  // ---------- Navigation ----------------------------------------

  const goNext = () => {
    setError("");
    const err = step === 1 ? validateStep1() : validateStep2();
    if (err) return setError(err);
    setStep((s) => (s === 1 ? 2 : 3));
  };
  const goBack = () => { setError(""); setStep((s) => (s === 3 ? 2 : 1)); };

  const addInvite = () => {
    if (!newInviteEmail.trim() || !/^\S+@\S+\.\S+$/.test(newInviteEmail)) {
      return setError(t("Enter a valid email.", "أدخل بريدًا إلكترونيًا صالحًا."));
    }
    if (invites.some((i) => i.email === newInviteEmail.trim())) {
      return setError(t("Already invited.", "تمت الدعوة مسبقًا."));
    }
    setError("");
    setInvites([...invites, { email: newInviteEmail.trim(), role: newInviteRole }]);
    setNewInviteEmail("");
  };

  const removeInvite = (email: string) => {
    setInvites(invites.filter((i) => i.email !== email));
  };

  // ---------- Submit --------------------------------------------

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    const err = validateStep3();
    if (err) return setError(err);

    const payload: OnboardingInput = {
      crNumber: crHit?.cr_number ?? crNumber.trim() ?? undefined,
      company: {
        name: companyName.trim(),
        name_ar: companyNameAr.trim() || undefined,
        email: companyEmail.trim(),
        phone: companyPhone.trim() || undefined,
        address: companyAddress.trim() || undefined,
        sector: crHit?.sector ?? (propertyType === "hotel" ? "hospitality" : "car_rental"),
      },
      firstProperty: {
        type: propertyType,
        name: propertyName.trim(),
      },
      owner: {
        password,
        fullName: ownerName.trim(),
      },
      invites,
    };

    setLoading(true);
    try {
      await commitOnboarding(payload);
      setSuccess(true);
      // Pick up the new org in AuthContext. If email confirmation is
      // required by the Supabase project, the user still can't sign in
      // until they click the email — but their org is created and will
      // be waiting for them.
      await refreshMemberships();
    } catch (e: any) {
      setError(e?.message || t("Something went wrong. Please try again.", "حدث خطأ ما. حاول مرة أخرى."));
    } finally {
      setLoading(false);
    }
  };

  // ---------- Render --------------------------------------------

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4" dir={isAr ? "rtl" : "ltr"}>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-gold/8" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-gold/5" />
      </div>

      <div className="relative w-full max-w-xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Link to="/" className="inline-flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-ink flex items-center justify-center">
              <i className="ri-door-open-line text-white text-lg"></i>
            </div>
            <span className={`font-bold text-xl text-ink ${isAr ? "font-tajawal" : "font-inter"}`}>
              ضيافة Diyafa
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-line-strong text-xs font-medium text-ink-soft hover:bg-[#F5EDD8] cursor-pointer transition-colors whitespace-nowrap ${isAr ? "font-tajawal flex-row-reverse" : ""}`}
            >
              <i className={`text-sm ${isAr ? "ri-arrow-right-line" : "ri-arrow-left-line"}`}></i>
              {t("Back to Home", "العودة للرئيسية")}
            </Link>
            <button
              onClick={toggleLang}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-line-strong text-xs font-medium text-ink-soft hover:bg-[#F5EDD8] cursor-pointer transition-colors"
            >
              <i className="ri-translate-2 text-sm"></i>
              {isAr ? "English" : "عربي"}
            </button>
          </div>
        </div>

        <div className="bg-white rounded-3xl overflow-hidden shadow-sm">
          <div className="h-1.5 w-full bg-gradient-to-r from-gold via-[#E8C060] to-gold-dark" />
          <div className="p-8">
            {success ? (
              <SuccessScreen
                email={companyEmail}
                t={t}
                isAr={isAr}
                onContinue={() => navigate("/dashboard")}
              />
            ) : (
              <>
                <StepHeader step={step} t={t} isAr={isAr} />

                {step === 1 && (
                  <Step1Company
                    {...{
                      crNumber, setCrNumber, crHit, crMiss, crManualEntry,
                      crLookupLoading, runCrLookup, switchToManualEntry,
                      companyName, setCompanyName, companyNameAr, setCompanyNameAr,
                      companyEmail, setCompanyEmail, companyPhone, setCompanyPhone,
                      companyAddress, setCompanyAddress,
                      propertyType, setPropertyType, propertyName, setPropertyName,
                      inputClass, labelClass, t, isAr,
                    }}
                  />
                )}

                {step === 2 && (
                  <Step2Team
                    {...{
                      ownerName, setOwnerName,
                      invites, newInviteEmail, setNewInviteEmail, newInviteRole, setNewInviteRole,
                      addInvite, removeInvite,
                      // Passing setError lets the email input clear the
                      // stale "Enter a valid email." the moment the user
                      // starts typing — no need for a parallel error
                      // channel inside Step2Team.
                      clearError: () => setError(""),
                      inputClass, labelClass, t, isAr,
                    }}
                  />
                )}

                {step === 3 && (
                  <Step3Review
                    {...{
                      companyName, companyEmail, propertyType, propertyName,
                      ownerName, inviteCount: invites.length,
                      password, setPassword, confirmPassword, setConfirmPassword,
                      showPass, setShowPass, termsAccepted, setTermsAccepted,
                      inputClass, labelClass, t, isAr,
                    }}
                  />
                )}

                {error && (
                  <div className="flex items-center gap-2 px-4 py-3 mt-4 rounded-xl bg-red-50 border border-red-100">
                    <i className="ri-error-warning-line text-red-400 text-sm flex-shrink-0"></i>
                    <p className={`text-xs text-red-500 ${isAr ? "font-tajawal" : ""}`}>{error}</p>
                  </div>
                )}

                {/* Footer nav */}
                <div className={`mt-6 flex items-center gap-3 ${isAr ? "flex-row-reverse" : ""}`}>
                  {step > 1 && (
                    <button
                      onClick={goBack}
                      disabled={loading}
                      className={`flex items-center gap-1.5 px-5 py-3 rounded-xl border border-line-strong text-sm font-medium text-ink-soft hover:bg-[#F5EDD8] cursor-pointer transition-colors disabled:opacity-60 ${isAr ? "font-tajawal flex-row-reverse" : ""}`}
                    >
                      <i className={`text-sm ${isAr ? "ri-arrow-right-line" : "ri-arrow-left-line"}`}></i>
                      {t("Back", "السابق")}
                    </button>
                  )}
                  <div className="flex-1" />
                  {step < 3 ? (
                    <button
                      onClick={goNext}
                      className={`flex items-center gap-2 px-6 py-3 rounded-xl bg-gold text-ink font-semibold text-sm hover:bg-gold-dark transition-colors whitespace-nowrap cursor-pointer ${isAr ? "font-tajawal flex-row-reverse" : ""}`}
                    >
                      {t("Continue", "متابعة")}
                      <i className={`text-sm ${isAr ? "ri-arrow-left-line" : "ri-arrow-right-line"}`}></i>
                    </button>
                  ) : (
                    <button
                      onClick={handleSubmit}
                      disabled={loading}
                      className={`flex items-center gap-2 px-6 py-3 rounded-xl bg-gold text-ink font-semibold text-sm hover:bg-gold-dark transition-colors whitespace-nowrap cursor-pointer disabled:opacity-70 ${isAr ? "font-tajawal flex-row-reverse" : ""}`}
                    >
                      {loading ? (
                        <>
                          <i className="ri-loader-4-line animate-spin text-base"></i>
                          {t("Creating your workspace...", "جارٍ إنشاء مساحة العمل...")}
                        </>
                      ) : (
                        <>
                          <i className="ri-check-line"></i>
                          {t("Create Account", "إنشاء حساب")}
                        </>
                      )}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <p className={`text-center text-xs text-muted mt-6 ${isAr ? "font-tajawal" : ""}`}>
          {t("Already have an account?", "لديك حساب بالفعل؟")}{" "}
          <Link to="/" className="text-gold font-semibold hover:text-gold-dark cursor-pointer">
            {t("Sign In", "تسجيل الدخول")}
          </Link>
        </p>
      </div>
    </div>
  );
}

// ================================================================
// Sub-components
// ================================================================

function StepHeader({ step, t, isAr }: { step: Step; t: (en: string, ar: string) => string; isAr: boolean }) {
  const titles: Record<Step, { en: string; ar: string; sub_en: string; sub_ar: string }> = {
    1: { en: "Tell us about your company", ar: "أخبرنا عن شركتك",
         sub_en: "Step 1 of 3 — company & first property",
         sub_ar: "الخطوة 1 من 3 — الشركة والمنشأة الأولى" },
    2: { en: "Add your team", ar: "أضف فريقك",
         sub_en: "Step 2 of 3 — owner & optional invites",
         sub_ar: "الخطوة 2 من 3 — المالك ودعوات اختيارية" },
    3: { en: "Review & create", ar: "مراجعة وإنشاء",
         sub_en: "Step 3 of 3 — password & confirmation",
         sub_ar: "الخطوة 3 من 3 — كلمة المرور والتأكيد" },
  };
  const title = titles[step];
  return (
    <div className="mb-6">
      {/* Progress bar */}
      <div className={`flex items-center gap-2 mb-4 ${isAr ? "flex-row-reverse" : ""}`}>
        {[1, 2, 3].map((n) => (
          <div
            key={n}
            className={`flex-1 h-1 rounded-full transition-colors ${n <= step ? "bg-gold" : "bg-line"}`}
          />
        ))}
      </div>
      <h2 className={`font-bold text-2xl text-ink ${isAr ? "font-tajawal" : "font-inter"}`}>
        {t(title.en, title.ar)}
      </h2>
      <p className={`text-sm text-muted mt-1 ${isAr ? "font-tajawal" : ""}`}>
        {t(title.sub_en, title.sub_ar)}
      </p>
    </div>
  );
}

// ---------- Step 1 ----------------------------------------------

function Step1Company(p: any) {
  const { t, isAr, inputClass, labelClass, crHit, crMiss, crManualEntry, crLookupLoading } = p;
  const showCompanyFields = crHit || crManualEntry;

  return (
    <div className="space-y-4">
      {/* ── CR lookup panel ─────────────────────────────────────── */}
      <div className="p-4 rounded-xl bg-cream border border-line">
        <div className={`flex items-center gap-2 mb-3 ${isAr ? "flex-row-reverse" : ""}`}>
          <div className="w-8 h-8 rounded-lg bg-gold/15 flex items-center justify-center flex-shrink-0">
            <i className="ri-shield-check-line text-gold text-base"></i>
          </div>
          <div>
            <p className={`text-sm font-semibold text-ink ${isAr ? "font-tajawal" : ""}`}>
              {t("Find your company", "ابحث عن شركتك")}
            </p>
            <p className={`text-xs text-muted ${isAr ? "font-tajawal" : ""}`}>
              {t("Enter your Commercial Registration (CR) number to auto-fill your details from the Oman registry.",
                 "أدخل رقم السجل التجاري لملء بياناتك تلقائيًا من السجل العُماني.")}
            </p>
          </div>
        </div>

        <div className={`flex gap-2 ${isAr ? "flex-row-reverse" : ""}`}>
          <input
            className={`${inputClass} flex-1`}
            value={p.crNumber}
            onChange={(e) => p.setCrNumber(e.target.value)}
            placeholder={t("e.g. 1234567", "مثال: 1234567")}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); p.runCrLookup(); } }}
            disabled={crLookupLoading || !!crHit}
          />
          {!crHit ? (
            <button
              type="button"
              onClick={p.runCrLookup}
              disabled={crLookupLoading}
              className={`px-5 rounded-xl bg-ink text-white text-sm font-semibold hover:bg-[#2A2A3E] cursor-pointer whitespace-nowrap disabled:opacity-60 ${isAr ? "font-tajawal" : ""}`}
            >
              {crLookupLoading ? (
                <i className="ri-loader-4-line animate-spin"></i>
              ) : (
                <><i className="ri-search-line"></i> {t("Look up", "ابحث")}</>
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                p.setCrNumber("");
                p.runCrLookup();     // resets crHit/crMiss inside
              }}
              className={`px-5 rounded-xl border border-line-strong text-ink-soft text-sm font-medium hover:bg-white cursor-pointer whitespace-nowrap ${isAr ? "font-tajawal" : ""}`}
            >
              <i className="ri-refresh-line"></i> {t("Change", "تغيير")}
            </button>
          )}
        </div>

        {/* Not-found state with manual-entry fallback */}
        {crMiss && !crHit && (
          <div className={`mt-3 p-3 rounded-lg bg-orange-50 border border-orange-200 ${isAr ? "text-right" : ""}`}>
            <p className={`text-xs text-orange-700 ${isAr ? "font-tajawal" : ""}`}>
              <i className="ri-information-line me-1"></i>
              {t(`No match for CR ${p.crNumber}. Double-check the number or enter your details manually.`,
                 `لا يوجد تطابق لسجل ${p.crNumber}. تحقق من الرقم أو أدخل بياناتك يدويًا.`)}
            </p>
            <button
              type="button"
              onClick={p.switchToManualEntry}
              className={`mt-1.5 text-xs font-semibold text-gold hover:underline cursor-pointer ${isAr ? "font-tajawal" : ""}`}
            >
              {t("Enter manually instead →", "إدخال يدوي بدلاً من ذلك ←")}
            </button>
          </div>
        )}

        {/* Found state — compact confirmation card */}
        {crHit && (
          <div className={`mt-3 p-3 rounded-lg bg-green-50 border border-green-200 ${isAr ? "text-right" : ""}`}>
            <div className={`flex items-start gap-2 ${isAr ? "flex-row-reverse" : ""}`}>
              <i className="ri-check-line text-green-600 text-base flex-shrink-0 mt-0.5"></i>
              <div className="min-w-0">
                <p className={`text-sm font-semibold text-ink truncate ${isAr ? "font-tajawal" : ""}`}>
                  {crHit.name_en}
                </p>
                {crHit.name_ar && (
                  <p className="font-tajawal text-xs text-muted truncate" dir="rtl">
                    {crHit.name_ar}
                  </p>
                )}
                <p className={`text-xs text-green-700 mt-1 ${isAr ? "font-tajawal" : ""}`}>
                  {t("Verified · Active in CR registry", "موثق · نشط في السجل التجاري")}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Manual-entry toggle (visible when nothing found AND user didn't click) */}
        {!crHit && !crMiss && !crManualEntry && (
          <button
            type="button"
            onClick={p.switchToManualEntry}
            className={`mt-2 text-xs text-muted hover:text-gold cursor-pointer ${isAr ? "font-tajawal" : ""}`}
          >
            {t("Don't have a CR yet? Enter details manually →", "ليس لديك سجل تجاري بعد؟ أدخل البيانات يدويًا ←")}
          </button>
        )}
      </div>

      {/* ── Company fields — appear after CR hit or manual entry ─ */}
      {showCompanyFields && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>{t("Company name (EN)", "اسم الشركة (إنجليزي)")} *</label>
              <input className={inputClass} value={p.companyName} onChange={(e) => p.setCompanyName(e.target.value)} placeholder={t("Al Muraqqa Hotel Group", "مجموعة فندق المرقاع")} />
            </div>
            <div>
              <label className={labelClass}>{t("Company name (AR)", "اسم الشركة (عربي)")}</label>
              <input className={inputClass} value={p.companyNameAr} onChange={(e) => p.setCompanyNameAr(e.target.value)} placeholder="مجموعة فندق المرقاع" dir="rtl" />
            </div>
          </div>

          <div>
            <label className={labelClass}>{t("Admin email", "البريد الإلكتروني للمالك")} *</label>
            <input className={inputClass} type="email" value={p.companyEmail} onChange={(e) => p.setCompanyEmail(e.target.value)} placeholder="owner@yourhotel.om" autoComplete="email" />
            <p className={`text-xs text-muted mt-1 ${isAr ? "font-tajawal" : ""}`}>
              {t("This becomes your sign-in email. We'll send a confirmation link.", "سيصبح هذا بريد تسجيل الدخول. سنرسل رابط تأكيد.")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>{t("Phone", "الهاتف")}</label>
              <input className={inputClass} value={p.companyPhone} onChange={(e) => p.setCompanyPhone(e.target.value)} placeholder="+968 24 000 000" />
            </div>
            <div>
              <label className={labelClass}>{t("Address", "العنوان")}</label>
              <input className={inputClass} value={p.companyAddress} onChange={(e) => p.setCompanyAddress(e.target.value)} placeholder={t("Muscat, Oman", "مسقط، عُمان")} />
            </div>
          </div>

          {/* Property type toggle */}
          <div>
            <label className={labelClass}>{t("What are you managing?", "ماذا تدير؟")} *</label>
            <div className={`grid grid-cols-2 gap-3 ${isAr ? "flex-row-reverse" : ""}`}>
              <button
                type="button"
                onClick={() => p.setPropertyType("hotel")}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-start ${p.propertyType === "hotel" ? "border-gold bg-gold-soft" : "border-line-strong hover:border-gold/60"}`}
              >
                <i className="ri-building-line text-2xl text-gold"></i>
                <div>
                  <p className={`font-semibold text-sm text-ink ${isAr ? "font-tajawal" : ""}`}>
                    {t("Hotel", "فندق")}
                  </p>
                  <p className={`text-xs text-muted ${isAr ? "font-tajawal" : ""}`}>
                    {t("Rooms, guests, check-in/out", "غرف، نزلاء، تسجيل دخول/مغادرة")}
                  </p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => p.setPropertyType("rental")}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-start ${p.propertyType === "rental" ? "border-gold bg-gold-soft" : "border-line-strong hover:border-gold/60"}`}
              >
                <i className="ri-car-line text-2xl text-gold"></i>
                <div>
                  <p className={`font-semibold text-sm text-ink ${isAr ? "font-tajawal" : ""}`}>
                    {t("Car Rental", "تأجير سيارات")}
                  </p>
                  <p className={`text-xs text-muted ${isAr ? "font-tajawal" : ""}`}>
                    {t("Fleet, drivers, agreements", "أسطول، سائقون، اتفاقيات")}
                  </p>
                </div>
              </button>
            </div>
            <p className={`text-xs text-muted mt-2 ${isAr ? "font-tajawal" : ""}`}>
              {t("You can add more (of either kind) after setup.", "يمكنك إضافة المزيد (من أي نوع) بعد الإعداد.")}
            </p>
          </div>

          <div>
            <label className={labelClass}>
              {p.propertyType === "hotel" ? t("Hotel name", "اسم الفندق") : t("Fleet/branch name", "اسم الأسطول/الفرع")} *
            </label>
            <input
              className={inputClass}
              value={p.propertyName}
              onChange={(e) => p.setPropertyName(e.target.value)}
              placeholder={p.propertyType === "hotel"
                ? t("Al Muraqqa Downtown", "المرقاع وسط المدينة")
                : t("Muscat Airport Fleet", "أسطول مطار مسقط")}
            />
          </div>
        </>
      )}
    </div>
  );
}

// ---------- Step 2 ----------------------------------------------

function Step2Team(p: any) {
  const { t, isAr, inputClass, labelClass } = p;
  const roleLabel = (role: InviteRow["role"]) => {
    const map: Record<InviteRow["role"], { en: string; ar: string }> = {
      manager:      { en: "Manager",       ar: "مدير" },
      front_desk:   { en: "Front Desk",    ar: "الاستقبال" },
      rental_agent: { en: "Rental Agent",  ar: "موظف تأجير" },
      housekeeping: { en: "Housekeeping",  ar: "التدبير المنزلي" },
      accounting:   { en: "Accounting",    ar: "المحاسبة" },
    };
    return t(map[role].en, map[role].ar);
  };

  return (
    <div className="space-y-5">
      <div>
        <label className={labelClass}>{t("Your full name (owner)", "اسمك الكامل (المالك)")} *</label>
        <input className={inputClass} value={p.ownerName} onChange={(e) => p.setOwnerName(e.target.value)} placeholder={t("Ahmed Al-Rashidi", "أحمد الراشدي")} autoComplete="name" />
        <p className={`text-xs text-muted mt-1 ${isAr ? "font-tajawal" : ""}`}>
          {t("You'll be the billing & admin contact for this workspace.", "ستكون جهة اتصال الفوترة والإدارة لمساحة العمل.")}
        </p>
      </div>

      <div>
        <label className={labelClass}>{t("Invite teammates (optional)", "ادع زملاء الفريق (اختياري)")}</label>
        {/* Email input on its own row so it has breathing room on narrow
         *  screens. Role + Add button sit below.                      */}
        <input
          className={`${inputClass} mb-2`}
          type="email"
          value={p.newInviteEmail}
          onChange={(e) => {
            p.setNewInviteEmail(e.target.value);
            // Clear any stale "Enter a valid email." the moment the
            // user starts typing — avoids a jarring red box sitting
            // under an input they're actively fixing.
            p.clearError?.();
          }}
          placeholder={t("teammate@yourhotel.om", "teammate@yourhotel.om")}
          onKeyDown={(e) => { if (e.key === "Enter" && p.newInviteEmail.trim()) { e.preventDefault(); p.addInvite(); } }}
        />
        <div className={`flex gap-2 ${isAr ? "flex-row-reverse" : ""}`}>
          <select
            className={`${inputClass} flex-1`}
            value={p.newInviteRole}
            onChange={(e) => p.setNewInviteRole(e.target.value as InviteRow["role"])}
          >
            <option value="manager">{t("Manager", "مدير")}</option>
            <option value="front_desk">{t("Front Desk", "الاستقبال")}</option>
            <option value="rental_agent">{t("Rental Agent", "موظف تأجير")}</option>
            <option value="housekeeping">{t("Housekeeping", "التدبير المنزلي")}</option>
            <option value="accounting">{t("Accounting", "المحاسبة")}</option>
          </select>
          <button
            type="button"
            onClick={p.addInvite}
            disabled={!p.newInviteEmail.trim()}
            className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-ink text-white text-sm font-semibold hover:bg-[#2A2A3E] cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed ${isAr ? "font-tajawal flex-row-reverse" : ""}`}
          >
            <i className="ri-add-line"></i>
            {t("Add invite", "إضافة")}
          </button>
        </div>
        <p className={`text-xs text-muted mt-1 ${isAr ? "font-tajawal" : ""}`}>
          {t("Skip this if you're setting up solo. Invites are sent after your account is created.",
             "تخطَّ هذا إذا كنت تعمل بمفردك. ترسل الدعوات بعد إنشاء حسابك.")}
        </p>
      </div>

      {p.invites.length > 0 && (
        <div className="border border-line rounded-xl divide-y divide-line">
          {p.invites.map((inv: InviteRow) => (
            <div key={inv.email} className={`flex items-center justify-between px-4 py-3 ${isAr ? "flex-row-reverse" : ""}`}>
              <div className={`flex items-center gap-3 ${isAr ? "flex-row-reverse" : ""}`}>
                <div className="w-8 h-8 rounded-full bg-gold-soft flex items-center justify-center">
                  <i className="ri-mail-line text-gold text-sm"></i>
                </div>
                <div>
                  <p className={`text-sm font-medium text-ink ${isAr ? "font-tajawal" : ""}`}>{inv.email}</p>
                  <p className={`text-xs text-muted ${isAr ? "font-tajawal" : ""}`}>{roleLabel(inv.role)}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => p.removeInvite(inv.email)}
                className="text-[#B0A898] hover:text-red-500 cursor-pointer text-sm"
                aria-label={t("Remove invite", "إزالة الدعوة")}
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Step 3 ----------------------------------------------

function Step3Review(p: any) {
  const { t, isAr, inputClass, labelClass } = p;
  return (
    <div className="space-y-5">
      {/* Summary card */}
      <div className="p-4 rounded-xl bg-cream border border-line space-y-2">
        <SummaryRow label={t("Company", "الشركة")} value={p.companyName} />
        <SummaryRow label={t("Admin email", "بريد المالك")} value={p.companyEmail} />
        <SummaryRow
          label={t("Type", "النوع")}
          value={p.propertyType === "hotel" ? t("Hotel", "فندق") : t("Car Rental", "تأجير سيارات")}
        />
        <SummaryRow
          label={p.propertyType === "hotel" ? t("First hotel", "أول فندق") : t("First fleet", "أول أسطول")}
          value={p.propertyName}
        />
        <SummaryRow label={t("Owner", "المالك")} value={p.ownerName} />
        <SummaryRow
          label={t("Team invites", "دعوات الفريق")}
          value={p.inviteCount > 0
            ? t(`${p.inviteCount} invited`, `${p.inviteCount} مدعو`)
            : t("None yet", "لا يوجد بعد")}
        />
      </div>

      <div>
        <label className={labelClass}>{t("Create password", "أنشئ كلمة مرور")} *</label>
        <div className="relative">
          <input
            className={`${inputClass} ${isAr ? "pl-10" : "pr-10"}`}
            type={p.showPass ? "text" : "password"}
            value={p.password}
            onChange={(e) => p.setPassword(e.target.value)}
            placeholder={t("Min. 8 characters", "8 أحرف على الأقل")}
            autoComplete="new-password"
          />
          <button
            type="button"
            onClick={() => p.setShowPass(!p.showPass)}
            className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-[#B0A898] hover:text-ink-soft cursor-pointer transition-colors ${isAr ? "left-3" : "right-3"}`}
          >
            <i className={`text-sm ${p.showPass ? "ri-eye-off-line" : "ri-eye-line"}`}></i>
          </button>
        </div>
      </div>

      <div>
        <label className={labelClass}>{t("Confirm password", "تأكيد كلمة المرور")} *</label>
        <input
          className={inputClass}
          type={p.showPass ? "text" : "password"}
          value={p.confirmPassword}
          onChange={(e) => p.setConfirmPassword(e.target.value)}
          placeholder={t("Repeat your password", "أعد كتابة كلمة المرور")}
          autoComplete="new-password"
        />
      </div>

      <label className={`flex items-start gap-3 cursor-pointer ${isAr ? "flex-row-reverse" : ""}`}>
        <input
          type="checkbox"
          checked={p.termsAccepted}
          onChange={(e) => p.setTermsAccepted(e.target.checked)}
          className="mt-1 w-4 h-4 accent-gold cursor-pointer"
        />
        <span className={`text-xs text-muted leading-relaxed ${isAr ? "font-tajawal" : ""}`}>
          {t("I agree to the", "أوافق على")}{" "}
          <span className="text-gold hover:underline">{t("Terms of Service", "شروط الخدمة")}</span>{" "}
          {t("and", "و")}{" "}
          <span className="text-gold hover:underline">{t("Privacy Policy", "سياسة الخصوصية")}</span>.
        </span>
      </label>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value?: string }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted">{label}</span>
      <span className="font-medium text-ink truncate max-w-[60%]">{value || "—"}</span>
    </div>
  );
}

// ---------- Success --------------------------------------------

function SuccessScreen({
  email,
  t,
  isAr,
  onContinue,
}: {
  email: string;
  t: (en: string, ar: string) => string;
  isAr: boolean;
  onContinue: () => void;
}) {
  return (
    <div className="text-center py-4">
      <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
        <i className="ri-mail-check-line text-green-500 text-3xl"></i>
      </div>
      <h2 className={`font-bold text-xl text-ink mb-2 ${isAr ? "font-tajawal" : "font-inter"}`}>
        {t("Your workspace is ready", "مساحة عملك جاهزة")}
      </h2>
      <p className={`text-sm text-muted mb-2 leading-relaxed ${isAr ? "font-tajawal" : ""}`}>
        {t("We sent a confirmation link to", "أرسلنا رابط تأكيد إلى")}{" "}
        <strong className="text-ink">{email}</strong>.
      </p>
      <p className={`text-xs text-muted mb-6 leading-relaxed ${isAr ? "font-tajawal" : ""}`}>
        {t("Click it to activate your account, then sign in to your dashboard.",
           "انقر عليه لتفعيل حسابك ثم سجّل دخولك إلى لوحة التحكم.")}
      </p>
      <button
        onClick={onContinue}
        className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gold text-ink font-semibold text-sm hover:bg-gold-dark transition-colors whitespace-nowrap cursor-pointer ${isAr ? "font-tajawal flex-row-reverse" : ""}`}
      >
        {t("Go to Dashboard", "انتقل إلى لوحة التحكم")}
        <i className={`text-sm ${isAr ? "ri-arrow-left-line" : "ri-arrow-right-line"}`}></i>
      </button>
    </div>
  );
}
